import { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack, TextField, Typography } from '@mui/material';

export interface CatalogLeadTimes {
  productionDuration: number;
  processingDuration: number;
  bufferDays: number;
  orderAmountDays: number;
  shipmentMethodId?: string;
}

interface CompanyLeadTimeDefaults {
  defaultProductionDuration?: number;
  defaultProcessingDuration?: number;
  defaultMinimumDaysOfStockInReserve?: number;
  defaultMinimumOrderAmountDays?: number;
  defaultShipmentMethodId?: string;
}

interface AddToCatalogDialogProps {
  open: boolean;
  busy: boolean;
  error: string | null;
  settings?: CompanyLeadTimeDefaults;
  shippingDays: number;
  onClose: () => void;
  onConfirm: (values: CatalogLeadTimes) => void;
}

const AddToCatalogDialog = ({ open, busy, error, settings, shippingDays, onClose, onConfirm }: AddToCatalogDialogProps) => {
  const processing = settings?.defaultProcessingDuration ?? 0;
  const [leadTimeDays, setLeadTimeDays] = useState((settings?.defaultProductionDuration ?? 0) + processing + shippingDays);
  const [bufferDays, setBufferDays] = useState(settings?.defaultMinimumDaysOfStockInReserve ?? 0);
  const [orderAmountDays, setOrderAmountDays] = useState(settings?.defaultMinimumOrderAmountDays ?? 0);

  useEffect(() => {
    if (!open) return;
    setLeadTimeDays((settings?.defaultProductionDuration ?? 0) + (settings?.defaultProcessingDuration ?? 0) + shippingDays);
    setBufferDays(settings?.defaultMinimumDaysOfStockInReserve ?? 0);
    setOrderAmountDays(settings?.defaultMinimumOrderAmountDays ?? 0);
  }, [open, settings, shippingDays]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="h6">Add to catalog</Typography>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <TextField size="small" type="number" label="Lead time (days)" value={leadTimeDays} onChange={event => setLeadTimeDays(Math.max(0, Number(event.target.value) || 0))} />
          <TextField size="small" type="number" label="Buffer (days)" value={bufferDays} onChange={event => setBufferDays(Math.max(0, Number(event.target.value) || 0))} />
          <TextField size="small" type="number" label="Order amount (days)" value={orderAmountDays} onChange={event => setOrderAmountDays(Math.max(0, Number(event.target.value) || 0))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => onConfirm({
            productionDuration: Math.max(0, leadTimeDays - processing - shippingDays),
            processingDuration: settings?.defaultProcessingDuration ?? 0,
            bufferDays,
            orderAmountDays,
            shipmentMethodId: settings?.defaultShipmentMethodId,
          })}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCatalogDialog;
